import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
    CloseOutlined,
    SaveOutlined,
} from "@mui/icons-material";
import {
    Alert,
    CircularProgress,
    Divider,
    Drawer,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    TextField,
    Tooltip,
} from "@mui/material";
import { useUpdateOrgMutation } from "state/rtkQuery/org/orgMutations";
import ChipArrayInput from "components/shared/ChipArrayInput/ChipArrayInput";

import "./OrgEditDrawer.css";

// ── Toggle row ────────────────────────────────────────────────────────────────

const ToggleRow = ({ label, description = null, warning = null, checked, onChange }) => (
    <div className="org-drawer__toggle-row">
        <div className="org-drawer__toggle-text">
            <span style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                <span className="org-drawer__toggle-label">{label}</span>
                {warning && (
                    <span style={{
                        fontSize: "11px",
                        fontWeight: 600,
                        color: "#b45309",
                        background: "#fef3c7",
                        border: "1px solid #fcd34d",
                        borderRadius: "4px",
                        padding: "1px 7px",
                        lineHeight: 1.5,
                    }}>
                        ⚠ {warning}
                    </span>
                )}
            </span>
            {description && (
                <span className="org-drawer__toggle-desc">{description}</span>
            )}
        </div>
        <FormControlLabel
            control={
                <Switch
                    checked={checked}
                    onChange={(e) => onChange(e.target.checked)}
                    size="small"
                    sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": { color: "#86BC25" },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                            backgroundColor: "#86BC25",
                        },
                    }}
                />
            }
            label={checked ? "Yes" : "No"}
            sx={{ margin: 0, "& .MuiFormControlLabel-label": { fontSize: "13px" } }}
        />
    </div>
);

ToggleRow.propTypes = {
    label: PropTypes.string.isRequired,
    description: PropTypes.string,
    warning: PropTypes.string,
    checked: PropTypes.bool.isRequired,
    onChange: PropTypes.func.isRequired,
};


// ── Section heading ───────────────────────────────────────────────────────────

const SectionHeading = ({ title }) => (
    <p className="org-drawer__section-title">{title}</p>
);

SectionHeading.propTypes = { title: PropTypes.string.isRequired };

// ── Build form state from org object ──────────────────────────────────────────

const buildForm = (org) => ({
    name: org?.name || "",
    description: org?.description || "",
    signup_banner: org?.signup_banner || "",
    org_type: org?.org_type || "",
    membership_ad_group: org?.membership_ad_group || "",
    admin_ad_group: org?.admin_ad_group || "",
    owners: Array.isArray(org?.owners) ? org.owners : [],
    owners_email: Array.isArray(org?.owners_email) ? org.owners_email : [],
    approvers: Array.isArray(org?.approvers) ? org.approvers : [],
    service_owner: org?.service_owner || "",
    countries: Array.isArray(org?.countries) ? org.countries : [],
    visible: org?.visible ?? true,
    approval_required: org?.approval_required ?? false,
    user_removal_enabled: org?.user_removal_enabled ?? false,
    inactive_user_removal_enabled: org?.inactive_user_removal_enabled ?? false,
    github_org_link: org?.additional_info?.github_org_link || "",
    custom_copilot_page: org?.additional_info?.custom_copilot_page || "",
    custom_org_website: org?.additional_info?.custom_org_website || "",
    org_sync_is_enabled: org?.org_sync_config?.is_enabled ?? false,
    org_sync_is_owners_sync_enabled: org?.org_sync_config?.is_owners_sync_enabled ?? false,
    org_sync_ignored_emails: Array.isArray(org?.org_sync_config?.ignored_emails)
        ? org.org_sync_config.ignored_emails
        : [],
    org_sync_owners_user_name: Array.isArray(org?.org_sync_config?.owners_user_name)
        ? org.org_sync_config.owners_user_name
        : [],
});

// ── Main component ────────────────────────────────────────────────────────────

const OrgEditDrawer = ({ open, org = null, onClose, onSaved }) => {
    const [form, setForm] = useState(() => buildForm(org));
    const [errors, setErrors] = useState({});

    const [updateOrg, { isLoading, isError, error, reset }] = useUpdateOrgMutation();

    // Re-populate form whenever the selected org changes
    useEffect(() => {
        if (org) {
            setForm(buildForm(org));
            setErrors({});
            reset();
        }
    }, [org]);

    const set = (field) => (val) =>
        setForm((f) => ({ ...f, [field]: val }));

    const setFromEvent = (field) => (e) => {
        setForm((f) => ({ ...f, [field]: e.target.value }));
        setErrors((err) => ({ ...err, [field]: undefined }));
    };

    const validate = () => {
        const e = {};
        if (!form.name.trim()) e.name = "Name is required.";
        if (form.owners.length === 0) e.owners = "At least one owner is required.";
        if (form.approvers.length === 0) e.approvers = "At least one approver is required.";
        if (form.countries.length === 0) e.countries = "At least one country is required.";
        return e;
    };

    const handleSave = async () => {
        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        const body = {
            name: form.name.trim(),
            description: form.description.trim() || undefined,
            signup_banner: form.signup_banner.trim() || undefined,
            org_type: form.org_type || undefined,
            membership_ad_group: form.membership_ad_group.trim() || undefined,
            admin_ad_group: form.admin_ad_group.trim() || undefined,
            owners: form.owners,
            owners_email: form.owners_email,
            approvers: form.approvers,
            service_owner: form.service_owner.trim() || undefined,
            countries: form.countries,
            visible: form.visible,
            approval_required: form.approval_required,
            user_removal_enabled: form.user_removal_enabled,
            inactive_user_removal_enabled: form.inactive_user_removal_enabled,
            additional_info: {
                github_org_link: form.github_org_link.trim() || undefined,
                custom_copilot_page: form.custom_copilot_page.trim() || undefined,
                custom_org_website: form.custom_org_website.trim() || undefined,
            },
            org_sync_config: {
                is_enabled: form.org_sync_is_enabled,
                is_owners_sync_enabled: form.org_sync_is_owners_sync_enabled,
                ignored_emails: form.org_sync_ignored_emails,
                owners_user_name: form.org_sync_owners_user_name,
            },
        };

        try {
            await updateOrg({ orgId: org._id, body }).unwrap();
            onSaved(`"${form.name}" updated successfully.`);
            onClose();
        } catch {
            // isError and error are set by RTK Query
        }
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{ sx: { width: { xs: "100%", sm: 580 }, overflowX: "hidden" } }}
        >
            {/* ── Header ─────────────────────────────────────────────────────── */}
            <div className="org-drawer__header">
                <div className="org-drawer__header-text">
                    <p className="org-drawer__header-eyebrow">ECR Ops · Org Management</p>
                    <h2 className="org-drawer__header-title">Edit Organisation</h2>
                    {org && <code className="org-drawer__header-id">{org._id}</code>}
                </div>
                <Tooltip title="Close">
                    <IconButton size="small" onClick={onClose} sx={{ color: "#9ca3af" }}>
                        <CloseOutlined />
                    </IconButton>
                </Tooltip>
            </div>

            {/* ── Scrollable body ─────────────────────────────────────────────── */}
            <div className="org-drawer__body">

                {/* 1. Basic Info */}
                <div className="org-drawer__section">
                    <SectionHeading title="Basic Information" />
                    <div className="org-drawer__fields">
                        <TextField
                            label="Organisation Name"
                            required
                            size="small"
                            value={form.name}
                            onChange={setFromEvent("name")}
                            error={!!errors.name}
                            helperText={errors.name}
                            fullWidth
                        />
                        <TextField
                            label="Description"
                            size="small"
                            value={form.description}
                            onChange={setFromEvent("description")}
                            multiline
                            minRows={2}
                            fullWidth
                        />
                        <TextField
                            label="Signup Banner"
                            size="small"
                            value={form.signup_banner}
                            onChange={setFromEvent("signup_banner")}
                            multiline
                            minRows={2}
                            fullWidth
                        />
                        <FormControl fullWidth size="small">
                            <InputLabel>Organisation Type</InputLabel>
                            <Select
                                value={form.org_type}
                                label="Organisation Type"
                                onChange={setFromEvent("org_type")}
                            >
                                <MenuItem value="US">US</MenuItem>
                                <MenuItem value="Global">Global</MenuItem>
                                <MenuItem value="Other">Other</MenuItem>
                            </Select>
                        </FormControl>
                    </div>
                </div>

                <Divider />

                {/* 2. AD Groups */}
                <div className="org-drawer__section">
                    <SectionHeading title="Active Directory Groups" />
                    <div className="org-drawer__fields">
                        <TextField
                            label="Membership AD Group"
                            size="small"
                            value={form.membership_ad_group}
                            onChange={setFromEvent("membership_ad_group")}
                            fullWidth
                        />
                        <TextField
                            label="Admin AD Group"
                            size="small"
                            value={form.admin_ad_group}
                            onChange={setFromEvent("admin_ad_group")}
                            fullWidth
                        />
                    </div>
                </div>

                <Divider />

                {/* 3. Owners & Approvers */}
                <div className="org-drawer__section">
                    <SectionHeading title="Owners & Approvers" />
                    <div className="org-drawer__fields">
                        <ChipArrayInput
                            label="Owners"
                            required
                            value={form.owners}
                            onChange={(v) => { set("owners")(v); setErrors((e) => ({ ...e, owners: undefined })); }}
                            error={!!errors.owners}
                            helperText={errors.owners}
                            placeholder="name@deloitte.com and press Enter…"
                        />
                        <ChipArrayInput
                            label="Owner Emails (notifications)"
                            value={form.owners_email}
                            onChange={set("owners_email")}
                            placeholder="name@deloitte.com and press Enter…"
                        />
                        <ChipArrayInput
                            label="Approvers"
                            required
                            value={form.approvers}
                            onChange={(v) => { set("approvers")(v); setErrors((e) => ({ ...e, approvers: undefined })); }}
                            error={!!errors.approvers}
                            helperText={errors.approvers}
                            placeholder="name@deloitte.com and press Enter…"
                        />
                        <TextField
                            label="Service Owner"
                            size="small"
                            value={form.service_owner}
                            onChange={setFromEvent("service_owner")}
                            fullWidth
                        />
                    </div>
                </div>

                <Divider />

                {/* 4. Location */}
                <div className="org-drawer__section">
                    <SectionHeading title="Location" />
                    <ChipArrayInput
                        label="Countries"
                        required
                        value={form.countries}
                        onChange={(v) => { set("countries")(v); setErrors((e) => ({ ...e, countries: undefined })); }}
                        error={!!errors.countries}
                        helperText={errors.countries}
                        placeholder="e.g. US, UK, IN and press Enter…"
                    />
                </div>

                <Divider />

                {/* 5. Additional Info */}
                <div className="org-drawer__section">
                    <SectionHeading title="Additional Info" />
                    <div className="org-drawer__fields">
                        <TextField
                            label="GitHub Org Link"
                            size="small"
                            value={form.github_org_link}
                            onChange={setFromEvent("github_org_link")}
                            fullWidth
                            placeholder="https://github.com/Deloitte-…"
                        />
                        <TextField
                            label="Copilot Requests Info"
                            size="small"
                            value={form.custom_copilot_page}
                            onChange={setFromEvent("custom_copilot_page")}
                            fullWidth
                            placeholder="https://…"
                        />
                        <TextField
                            label="Organization Website"
                            size="small"
                            value={form.custom_org_website}
                            onChange={setFromEvent("custom_org_website")}
                            fullWidth
                            placeholder="https://…"
                        />
                    </div>
                </div>

                <Divider />

                {/* 6. Settings */}
                <div className="org-drawer__section">
                    <SectionHeading title="Settings" />
                    <div className="org-drawer__toggles">
                        <ToggleRow label="Visible" description="Show this org in the portal for users to discover." checked={form.visible} onChange={set("visible")} />
                        <Divider />
                        <ToggleRow label="Approval Required" description="Membership requests must be approved before access is granted." checked={form.approval_required} onChange={set("approval_required")} />
                        <Divider />
                        <ToggleRow label="User Removal Enabled" description="Allow automated removal of users who no longer meet criteria." checked={form.user_removal_enabled} onChange={set("user_removal_enabled")} />
                        <Divider />
                        <ToggleRow label="Inactive User Removal" description="Automatically remove users who have been inactive too long." checked={form.inactive_user_removal_enabled} onChange={set("inactive_user_removal_enabled")} />
                    </div>
                </div>

                <Divider />

                {/* 7. Org Sync Config */}
                <div className="org-drawer__section">
                    <SectionHeading title="Org Sync Configuration" />
                    <div className="org-drawer__fields">
                        <div className="org-drawer__toggles">
                            <ToggleRow label="Org Sync Enabled" description="Sync all org info and membership from AD." warning="Disabling this turns off the entire sync for this org" checked={form.org_sync_is_enabled} onChange={set("org_sync_is_enabled")} />
                            <Divider />
                            <ToggleRow label="Org Owners Email Sync Enabled" description="Sync org owner emails automatically." checked={form.org_sync_is_owners_sync_enabled} onChange={set("org_sync_is_owners_sync_enabled")} />
                        </div>
                        <ChipArrayInput
                            label="Ignored Emails (sync)"
                            value={form.org_sync_ignored_emails}
                            onChange={set("org_sync_ignored_emails")}
                            placeholder="email@deloitte.com and press Enter…"
                        />
                        <ChipArrayInput
                            label="Owners Username (sync)"
                            value={form.org_sync_owners_user_name}
                            onChange={set("org_sync_owners_user_name")}
                            placeholder="github-username and press Enter…"
                        />
                    </div>
                </div>

                {/* Form error */}
                {isError && (
                    <div className="org-drawer__section">
                        <Alert severity="error" sx={{ fontSize: "13px" }}>
                            {error?.data?.message || "Failed to update organisation. Please try again."}
                        </Alert>
                    </div>
                )}
            </div>

            {/* ── Footer ─────────────────────────────────────────────────────── */}
            <div className="org-drawer__footer">
                <button
                    type="button"
                    className="org-drawer__btn org-drawer__btn--cancel"
                    onClick={onClose}
                    disabled={isLoading}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    className="org-drawer__btn org-drawer__btn--save"
                    onClick={handleSave}
                    disabled={isLoading}
                >
                    {isLoading ? (
                        <CircularProgress size={14} sx={{ color: "#fff", mr: 1 }} />
                    ) : (
                        <SaveOutlined style={{ fontSize: 16, marginRight: 6 }} />
                    )}
                    {isLoading ? "Saving…" : "Save Changes"}
                </button>
            </div>
        </Drawer>
    );
};

OrgEditDrawer.propTypes = {
    open: PropTypes.bool.isRequired,
    org: PropTypes.shape({
        _id: PropTypes.string,
        name: PropTypes.string,
        description: PropTypes.string,
        signup_banner: PropTypes.string,
        org_type: PropTypes.string,
        membership_ad_group: PropTypes.string,
        admin_ad_group: PropTypes.string,
        owners: PropTypes.arrayOf(PropTypes.string),
        owners_email: PropTypes.arrayOf(PropTypes.string),
        approvers: PropTypes.arrayOf(PropTypes.string),
        service_owner: PropTypes.string,
        countries: PropTypes.arrayOf(PropTypes.string),
        visible: PropTypes.bool,
        approval_required: PropTypes.bool,
        user_removal_enabled: PropTypes.bool,
        inactive_user_removal_enabled: PropTypes.bool,
        additional_info: PropTypes.shape({ github_org_link: PropTypes.string }),
        org_sync_config: PropTypes.shape({
            is_enabled: PropTypes.bool,
            is_owners_sync_enabled: PropTypes.bool,
            ignored_emails: PropTypes.arrayOf(PropTypes.string),
            owners_user_name: PropTypes.arrayOf(PropTypes.string),
        }),
    }),
    onClose: PropTypes.func.isRequired,
    onSaved: PropTypes.func.isRequired,
};



export default OrgEditDrawer;
